import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginEmail, registerEmail } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await loginEmail(email, password);
      else await registerEmail(email, password, name);
      navigate("/feed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-[#0b0c10] overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none telesis-tape" />
        <div className="relative z-10">
          <p className="telesis-eyebrow">Telesis · CAI Protocol</p>
          <h1 className="mt-4 text-4xl lg:text-5xl font-black tracking-tight leading-tight text-slate-50">
            A social ledger<br />where <span className="text-yellow-400">feeling</span> and <br /><span className="text-emerald-400">finance</span> redistribute together.
          </h1>
        </div>
        <div className="relative z-10 space-y-4 text-slate-400 text-sm font-mono">
          <p>— 20.21 CAD / mo</p>
          <p>— Unlimited Mentational Faculties</p>
          <p>— 15% Pain &amp; Suffering · 60% Redistribution Pool</p>
          <p>— 25% of every membership to Steven Craig Boileau</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#0e1017]">
        <div className="w-full max-w-md telesis-panel rounded p-8 telesis-rise">
          <p className="telesis-eyebrow">{mode === "login" ? "Return" : "Register"}</p>
          <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-6 text-slate-50">
            {mode === "login" ? "Enter the mentation" : "Join Telesis"}
          </h2>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
                <Input
                  data-testid={AUTH.registerName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
              <Input
                data-testid={mode === "login" ? AUTH.loginEmail : AUTH.registerEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 bg-[#12141c] border-[#222634] text-slate-100"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Password</Label>
              <Input
                data-testid={mode === "login" ? AUTH.loginPassword : AUTH.registerPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1 bg-[#12141c] border-[#222634] text-slate-100"
              />
            </div>
            <Button
              data-testid={mode === "login" ? AUTH.loginSubmit : AUTH.registerSubmit}
              type="submit"
              disabled={busy}
              className="w-full bg-yellow-400 text-black hover:bg-yellow-300 rounded-full font-semibold"
            >
              {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-500">
            <div className="flex-1 h-px bg-[#222634]" />
            <span className="font-mono">OR</span>
            <div className="flex-1 h-px bg-[#222634]" />
          </div>
          <Button
            data-testid={AUTH.googleLogin}
            onClick={googleLogin}
            variant="outline"
            className="w-full rounded-full bg-transparent border-[#2a2f3f] text-slate-100 hover:bg-[#1a1d28]"
          >
            Continue with Google
          </Button>
          <div className="mt-6 text-sm text-slate-400 text-center">
            {mode === "login" ? (
              <button
                data-testid={AUTH.toggleRegister}
                className="underline hover:text-yellow-400"
                onClick={() => setMode("register")}
              >
                No account yet? Register
              </button>
            ) : (
              <button
                data-testid={AUTH.toggleLogin}
                className="underline hover:text-yellow-400"
                onClick={() => setMode("login")}
              >
                Already a member? Sign in
              </button>
            )}
          </div>
          <p className="mt-6 text-xs text-slate-500 font-mono text-center">
            <Link to="/" className="hover:text-yellow-400">← Return to lobby</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
